/* eslint-disable @next/next/no-img-element */
import { ParsedLocale } from 'lib/constants/localization'
import _useTranslation from 'lib/hooks/_useTranslation'
import { trackEvent } from 'lib/tracking/analytics-service'
import { Subject } from 'interfaces/Subject'
import { AnalyticsEventKnowSEOSubjectClick } from 'lib/constants/AnalyticsEventType'
import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import { formatSubjectLabel } from 'lib/formatting/subjects'
import theme from 'stories/utils/theme'
import { loadingAreas } from 'components/feature/Authentication/AuthGoogle'
import { searchResultRouteOptions } from 'lib/constants/routes'
import Link from 'next/link'
import { useState, useEffect } from 'react'
import { trackPromise } from 'react-promise-tracker'
import { ApiError } from 'services/ApiService'
import SubjectService from 'services/SubjectService'

const SubjectBodyCard = styled.div<{ color: string }>`
    border-radius: 24px;
    background: radial-gradient(50% 50% at 50% 0%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%),
        ${(props) => props.color};
    width: 244px;
    height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Element = styled.div<{
    readonly iconColorFilter: string
}>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    p {
        word-break: break-all;
    }
    & .icon {
        filter: ${(props) => props.iconColorFilter};

        img {
            filter: ${(props) => props.iconColorFilter};
        }
    }
`

const Title = styled.h4`
    font-family: var(--font-inter);
    font-weight: 650;
    font-size: 17px;
    line-height: 20px;
    letter-spacing: 0.3px;
    color: ${theme.colors.lightGrayBackground};
    margin: 0;
    text-transform: capitalize;
`

const returnSubjectBackground = (category: string) => {
    switch (category) {
        case 'GREEN':
            return '#143515'
        case 'ORANGE':
            return '#4B2203'
        case 'FUCHSIA':
            return '#500E5B'
        case 'YELLOW':
            return '#03352F'
        default:
            return '#212A63'
    }
}

const returnSubjectIconBaseColor = (category: string) => {
    switch (category) {
        case 'GREEN':
            return '#439746'
        case 'ORANGE':
            return '#D66300'
        case 'FUCHSIA':
            return '#D83CF3'
        case 'YELLOW':
            return '#059889'
        default:
            return '#797FDD'
    }
}

const SubjectCard = ({ subject }: { subject: Subject }) => {
    const background = returnSubjectBackground(subject.colorCategory)
    const iconBaseColor = returnSubjectIconBaseColor(subject.colorCategory)
    const iconColorFilter = () => {
        switch (iconBaseColor) {
            case '#439746':
                return 'invert(50%) sepia(7%) saturate(3371%) hue-rotate(72deg) brightness(98%) contrast(85%)'
            case '#D66300':
                return 'invert(46%) sepia(99%) saturate(661%) hue-rotate(351deg) brightness(73%) contrast(107%)'
            case '#D83CF3':
                return 'invert(22%) sepia(86%) saturate(4508%) hue-rotate(285deg) brightness(156%) contrast(91%)'
            case '#059889':
                return 'invert(38%) sepia(98%) saturate(591%) hue-rotate(131deg) brightness(96%) contrast(96%)'
            case '#797FDD':
            default:
                return 'invert(46%) sepia(37%) saturate(680%) hue-rotate(199deg) brightness(104%) contrast(94%)'
        }
    }
    return (
        <SubjectBodyCard color={background}>
            <Element iconColorFilter={iconColorFilter()}>
                <div className="icon">
                    <img src={subject.iconUrl} alt={subject.name} width={50} height={50} />
                </div>

                <Title>{formatSubjectLabel(subject)}</Title>
            </Element>
        </SubjectBodyCard>
    )
}

const SubjectGrid = styled.div`
    max-width: 1024px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 16px;
`

interface Props {
    parsedLocale: ParsedLocale
}
const DarkKnowunitySubjects = ({ parsedLocale }: Props) => {
    const [mainSubjects, setMainSubjects] = useState<Subject[] | null>(null)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')

    useEffect(() => {
        trackPromise(
            SubjectService.list(parsedLocale.countryCode)
                .then((subjectList) => {
                    setMainSubjects(subjectList.slice(0, 8))
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'subject.list')
                    } else {
                        throw error
                    }
                }),
            loadingAreas.container
        )
    }, [parsedLocale.countryCode, tCommon])

    const onKnowSeoSubjectClick = (subject: Subject) => {
        trackEvent(AnalyticsEventKnowSEOSubjectClick, { value: subject.name, valueId: subject.id })
    }

    return (
        <SectionContainer
            backgroundColor="#1A1A1A"
            title={t('knows/subjectsSectionTitle')}
            borderRadius="48px"
            fullWidth
            mobileBorderRadius="32px"
        >
            <SubjectGrid>
                {mainSubjects?.length &&
                    mainSubjects?.map((subject) => (
                        <Link
                            href={searchResultRouteOptions('', subject.id)}
                            key={subject.id}
                            onClick={() => onKnowSeoSubjectClick(subject)}
                        >
                            <SubjectCard subject={subject} />
                        </Link>
                    ))}
            </SubjectGrid>
        </SectionContainer>
    )
}

export default DarkKnowunitySubjects
